import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "./api";

// Sprint-10 Phase C — cookie-only auth (no more localStorage). The
// HttpOnly `access_token` cookie is set by /api/auth/login and cleared
// by /api/auth/logout; the React app never sees the JWT itself. The
// canonical user shape is whatever /api/auth/me returns.
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/api/auth/me");
        setUser(data.user);
      } catch {
        setUser(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function login(email, password) {
    // POST /api/auth/login does two things: (1) sets the HttpOnly
    // access_token cookie via Set-Cookie, and (2) returns the
    // canonical user payload in the response body — byte-identical
    // to GET /api/auth/me.
    //
    // We seed AuthContext from the response body directly. Before
    // 2026-02-19 this code re-fetched /me to "verify the cookie
    // landed," but that introduced a cookie-vs-fetch race under
    // cross-site SameSite=None;Secure cookies: the browser
    // occasionally hadn't committed Set-Cookie to its jar by the
    // time the immediate next request fired. The cure was worse
    // than the disease — if the cookie really was rejected, the
    // very next dashboard fetch surfaces it immediately (every
    // request 401s), so the redundant /me probe wasn't adding
    // real defense. Eliminates the iteration-2–5 carryover flake.
    const { data } = await api.post("/api/auth/login", { email, password });
    setUser(data.user);
    return data.user;
  }

  async function logout() {
    try {
      await api.post("/api/auth/logout");
    } catch {
      /* ignore network errors on logout — we still clear local state */
    }
    setUser(null);
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
